import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * LCP helpers for SEO Discovery hero images (city / venue / performer hubs).
 *
 * The hero <img> is the LCP element on hub pages with an image, but it was
 * discovered late (no preload, no priority hint) and often lives on a
 * third-party host (Unsplash / Openverse). A head preload with
 * fetchpriority=high lets the browser open the connection and fetch the image
 * before the body parses — the same pattern ListingImageGallery uses for
 * listing-page LCP images.
 */

/**
 * Responsive srcset for hosts that support arbitrary resizing via a `w` query
 * param (Unsplash). Returns null for other hosts (Openverse images are
 * arbitrary origins we cannot resize).
 */
export const heroImageSrcSet = url => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== 'images.unsplash.com') return null;
    return [480, 768, 1080, 1600]
      .map(w => {
        const variant = new URL(parsed.toString());
        variant.searchParams.set('w', String(w));
        return `${variant.toString()} ${w}w`;
      })
      .join(', ');
  } catch (e) {
    return null;
  }
};

/** Hero images render full-bleed inside .heroImageFrame. */
export const HERO_IMAGE_SIZES = '100vw';

const SeoHeroImagePreload = ({ url }) => {
  if (!url) return null;
  const srcSet = heroImageSrcSet(url);
  const responsiveMaybe = srcSet ? { imagesrcset: srcSet, imagesizes: HERO_IMAGE_SIZES } : {};
  return (
    <Helmet>
      <link rel="preload" as="image" href={url} {...responsiveMaybe} fetchpriority="high" />
    </Helmet>
  );
};

export default SeoHeroImagePreload;
